import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useMemo, useState } from "react";

import {
  ConfigMigrationFeatureGroup,
  MigrationEnv,
  type StartConfigMigrationInput,
  useConfigMigrationCompaniesQuery,
  useConfigMigrationFeatureGroupsQuery,
} from "../../generated/graphql";
import {
  ENV_LABELS,
  isWriteConfirmed,
  requiresWriteConfirmation,
  SOURCE_ENV_OPTIONS,
  TARGET_ENV_OPTIONS,
} from "./config-migration-env-options";

const FEATURE_GROUP_LABELS: Record<ConfigMigrationFeatureGroup, string> = {
  [ConfigMigrationFeatureGroup.ApiUsers]: "API users",
  [ConfigMigrationFeatureGroup.CompanySettings]: "Company settings",
  [ConfigMigrationFeatureGroup.Tariffs]: "Tariffs",
  [ConfigMigrationFeatureGroup.Accessorials]: "Accessorials",
  [ConfigMigrationFeatureGroup.WarehousesTerminals]: "Warehouses & terminals",
  [ConfigMigrationFeatureGroup.Integrations]: "Integrations",
};

const styles = {
  form: { maxWidth: 560 },
  envRow: { gap: 2 },
  groups: { mt: 1 },
  prodWarning: { mt: 1 },
} satisfies Record<string, SxProps<Theme>>;

type CompanyOption = { uuid: string; name: string };

/** Props for {@link ConfigMigrationForm}. */
export type ConfigMigrationFormProps = {
  /** Called with the validated input when the operator runs a migration. */
  readonly onSubmit: (input: StartConfigMigrationInput) => void;
  /** True while a run is in flight (disables the submit button). */
  readonly submitting: boolean;
};

/**
 * Form for configuring a migration: source and target environments, the
 * company (loaded from the source env), feature groups, dry-run, and the
 * write confirmation for live targets (production and demo). Loads its own
 * option data and emits a fully built `StartConfigMigrationInput` via
 * `onSubmit`.
 */
const ConfigMigrationForm = ({
  onSubmit,
  submitting,
}: ConfigMigrationFormProps) => {
  const [source, setSource] = useState<MigrationEnv | "">("");
  const [target, setTarget] = useState<MigrationEnv | "">("");
  const [company, setCompany] = useState<CompanyOption | null>(null);
  const [groups, setGroups] = useState<ConfigMigrationFeatureGroup[]>([]);
  const [dryRun, setDryRun] = useState(true);
  const [confirmedTarget, setConfirmedTarget] = useState<MigrationEnv | null>(
    null,
  );

  const { data: featureGroupsData } = useConfigMigrationFeatureGroupsQuery();
  const featureGroups = featureGroupsData?.configMigrationFeatureGroups ?? [];

  const { data: companiesData, loading: companiesLoading } =
    useConfigMigrationCompaniesQuery({
      variables: { env: source === "" ? MigrationEnv.Staging : source },
      skip: source === "",
    });
  const needsWriteConfirm =
    target !== "" && requiresWriteConfirmation(target, dryRun);
  // Null when no target is selected, which is also when the confirmation is not
  // rendered, so the label doubles as the render guard.
  const writeConfirmLabel =
    target === "" || !needsWriteConfirm
      ? null
      : `I understand this writes to ${ENV_LABELS[target].toUpperCase()}.`;

  const canSubmit =
    source !== "" &&
    target !== "" &&
    company !== null &&
    groups.length > 0 &&
    (!needsWriteConfirm || isWriteConfirmed(target, confirmedTarget)) &&
    !submitting;

  const toggleGroup = (group: ConfigMigrationFeatureGroup) => {
    setGroups((current) =>
      current.includes(group)
        ? current.filter((value) => value !== group)
        : [...current, group],
    );
  };

  const handleSubmit = () => {
    if (source === "" || target === "" || company === null) {
      return;
    }
    onSubmit({
      source,
      target,
      companyUuid: company.uuid,
      groups,
      dryRun,
      confirmProductionTarget: isWriteConfirmed(target, confirmedTarget),
    });
  };

  const sortedCompanies = useMemo(() => {
    const list = companiesData?.configMigrationCompanies ?? [];
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [companiesData]);

  return (
    <Stack spacing={2.5} sx={styles.form}>
      <Stack direction="row" sx={styles.envRow}>
        <TextField
          select
          fullWidth
          label="Source environment"
          value={source}
          onChange={(event) => {
            setSource(event.target.value as MigrationEnv);
            setCompany(null);
          }}
        >
          {SOURCE_ENV_OPTIONS.map((env) => (
            <MenuItem key={env} value={env}>
              {ENV_LABELS[env]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          label="Target environment"
          value={target}
          onChange={(event) => {
            setTarget(event.target.value as MigrationEnv);
          }}
        >
          {TARGET_ENV_OPTIONS.map((env) => (
            <MenuItem key={env} value={env}>
              {ENV_LABELS[env]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {target === MigrationEnv.Demo ? (
        <Alert severity="info">
          Companies are listed from the source environment. A company that does
          not exist in demo yet can only be created by including the Company
          settings group, otherwise the run fails.
        </Alert>
      ) : null}

      <Autocomplete
        options={sortedCompanies}
        value={company}
        loading={companiesLoading}
        disabled={source === ""}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, selected) =>
          option.uuid === selected.uuid
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Company"
            helperText={
              source === "" ? "Select a source environment first." : undefined
            }
          />
        )}
        onChange={(_event, value) => {
          setCompany(value);
        }}
      />

      <Box>
        <Typography variant="subtitle2">Configuration to migrate</Typography>
        <FormGroup sx={styles.groups}>
          {featureGroups.map((group) => (
            <FormControlLabel
              key={group}
              label={FEATURE_GROUP_LABELS[group]}
              control={
                <Checkbox
                  checked={groups.includes(group)}
                  onChange={() => {
                    toggleGroup(group);
                  }}
                />
              }
            />
          ))}
        </FormGroup>
      </Box>

      <FormControlLabel
        label="Dry run (preview only, nothing is written)"
        control={
          <Switch
            checked={dryRun}
            onChange={(event) => {
              setDryRun(event.target.checked);
            }}
          />
        }
      />

      {writeConfirmLabel === null ? null : (
        <Alert severity="warning" sx={styles.prodWarning}>
          <FormControlLabel
            label={writeConfirmLabel}
            control={
              <Checkbox
                checked={isWriteConfirmed(target, confirmedTarget)}
                onChange={(event) => {
                  setConfirmedTarget(
                    event.target.checked && target !== "" ? target : null,
                  );
                }}
              />
            }
          />
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {dryRun ? "Run dry run" : "Run migration"}
        </Button>
      </Box>
    </Stack>
  );
};

export default ConfigMigrationForm;
